/*
  Join modal (mirrors donate-modal UX).

  Tabs:
    "join"      — records participation; name + email required for guests.
    "subscribe" — pick an ambassador tier and hand over to the ambassador
                  modal (window.GalasAmbassador). Without it, the old path:
                  cart/add.js with selling_plan -> /checkout.

  Member record:
    On first join a unique member code is generated (GLS-XXXXXX) and kept in
    localStorage under `galas:member`. Every [data-join-trigger] then shows
    "<code> ✓" instead of its label. The record is mirrored into Google Sheets
    via the Apps Script endpoint configured in theme settings (same one the
    contact / partnership forms use), and a Shopify customer is created with
    tags `join-event` + `member-<code>`.

  Opens from any [data-join-trigger] (may pass data-join-event-id /
  data-join-event-title) or via URL param ?join_modal=join|subscribe.
*/
(function () {
  'use strict';

  var MEMBER_KEY = 'galas:member';
  var TAB_ORDER = { join: 0, subscribe: 1 };

  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  var modalEl = null;
  var panelEl = null;
  var panelsAreaEl = null;
  var formEl = null;
  var successEl = null;
  var successCodeEl = null;
  var titleEl = null;

  var subChipsEl = null;
  var subPlans = {};
  var subVariants = {};

  var joinChipsEl = null;
  var joinCustomInputEl = null;
  var donateLinkEl = null;
  var donateLabelEl = null;
  var joinDefaults = { jarUrl: '', currency: '\u20b4', defaultAmount: '' };

  var sheets = { endpoint: '', token: '' };

  var state = {
    open: false,
    titleDefault: '',
    titleOpen: '',
    lastOpener: null,
    currentTab: 'join',
    eventId: '',
    subAmount: null,
    joinAmount: null,
    joinIsOther: false,
    isLoggedIn: false,
    customer: null,
    customerName: ''
  };

  function init() {
    modalEl = document.querySelector('[data-join-modal]');
    if (!modalEl) return;

    panelEl = modalEl.querySelector('[data-join-modal-panel]');
    panelsAreaEl = modalEl.querySelector('.join-modal__panels-area');
    formEl = modalEl.querySelector('[data-join-modal-form]');
    successEl = modalEl.querySelector('[data-join-success]');
    successCodeEl = modalEl.querySelector('[data-join-success-code]');
    titleEl = modalEl.querySelector('[data-join-modal-title]');
    state.titleDefault = titleEl ? titleEl.textContent.trim() : '';
    subChipsEl = modalEl.querySelector('[data-join-sub-chips]');
    joinChipsEl = modalEl.querySelector('[data-join-amounts]');
    joinCustomInputEl = modalEl.querySelector('[data-join-custom-input]');
    donateLinkEl = modalEl.querySelector('[data-join-donate-link]');
    donateLabelEl = modalEl.querySelector('[data-join-donate-label]');

    sheets.endpoint = modalEl.getAttribute('data-join-sheets-endpoint') || '';
    sheets.token = modalEl.getAttribute('data-join-sheets-token') || '';

    joinDefaults.jarUrl = modalEl.getAttribute('data-join-jar-url') || '';
    joinDefaults.currency = modalEl.getAttribute('data-join-currency') || '\u20b4';
    joinDefaults.defaultAmount = modalEl.getAttribute('data-join-default-amount') || '';

    try { subPlans = JSON.parse(modalEl.getAttribute('data-sub-plans') || '{}'); } catch (_) { subPlans = {}; }
    try { subVariants = JSON.parse(modalEl.getAttribute('data-sub-variants') || '{}'); } catch (_) { subVariants = {}; }

    var g = window.__galas || {};
    state.isLoggedIn = !!(g.customer);
    state.customer = g.customer || null;
    state.customerName = g.customerName || (g.customer && g.customer.first_name) || '';

    setupEvents();
    initSubAmounts();
    initJoinAmount();
    updateSubCta();
    markJoinedTriggers();
    reopenFromUrl();
  }

  /* Open via URL param (?join_modal=join|subscribe) — used by QR codes */
  function reopenFromUrl() {
    var params = new URLSearchParams(window.location.search);
    var tab = params.get('join_modal');
    if (!tab) return;
    if (!(tab in TAB_ORDER)) tab = 'join';

    params.delete('join_modal');
    var qs = params.toString();
    var cleanUrl = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
    window.history.replaceState(null, '', cleanUrl);

    open(null, tab);

    // Returning member opening the join tab — greet with their code right away.
    var member = loadMember();
    if (tab === 'join' && member && member.code) {
      showSuccess(member.code, null, member.name);
    }
  }

  function setupEvents() {
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-join-trigger]');
      if (trigger) {
        e.preventDefault();
        if (!trigger.classList.contains('is-joined')) openFromTrigger(trigger);
      }
    });

    modalEl.querySelectorAll('[data-join-modal-close]').forEach(function (el) {
      el.addEventListener('click', function (e) { e.preventDefault(); close(); });
    });

    modalEl.querySelectorAll('[data-join-tab]').forEach(function (tab) {
      tab.addEventListener('click', function (e) {
        e.preventDefault();
        switchTab(tab.getAttribute('data-join-tab'));
      });
    });

    if (formEl) formEl.addEventListener('submit', onJoinSubmit);

    modalEl.querySelectorAll('[data-join-freq-input]').forEach(function (input) {
      input.addEventListener('change', updateSubCta);
    });

    if (subChipsEl) {
      subChipsEl.addEventListener('click', function (e) {
        var chip = e.target.closest('[data-join-sub-amount]');
        if (!chip) return;
        e.preventDefault();
        selectSubChip(chip);
      });
    }

    if (joinChipsEl) {
      joinChipsEl.addEventListener('click', function (e) {
        var chip = e.target.closest('[data-join-amount]');
        if (!chip) return;
        e.preventDefault();
        selectJoinChip(chip);
      });
    }

    if (joinCustomInputEl) {
      joinCustomInputEl.addEventListener('focus', function () {
        var chip = joinCustomInputEl.closest('[data-join-amount="other"]');
        if (chip) {
          state.joinIsOther = true;
          activateJoinChip(chip);
          state.joinAmount = parseAmount(joinCustomInputEl.value);
        }
      });
      joinCustomInputEl.addEventListener('input', function () {
        state.joinAmount = parseAmount(joinCustomInputEl.value);
      });
    }

    var subscribeBtn = modalEl.querySelector('[data-join-subscribe]');
    if (subscribeBtn) subscribeBtn.addEventListener('click', onSubscribeClick);
  }

  function openFromTrigger(trigger) {
    state.eventId = trigger.getAttribute('data-join-event-id') || '';
    state.titleOpen = trigger.getAttribute('data-join-event-title') || '';
    open(trigger, 'join');
  }

  function open(opener, initialTab) {
    if (state.open) return;
    state.open = true;
    state.lastOpener = opener || null;

    switchTab(initialTab || 'join', true);
    clearErrors();
    resetContactFields();
    applyLoginState();
    hideSuccess();
    initSubAmounts();
    initJoinAmount();
    updateSubCta();

    modalEl.hidden = false;
    requestAnimationFrame(function () {
      modalEl.classList.add('is-open');
      modalEl.setAttribute('aria-hidden', 'false');
    });

    document.documentElement.classList.add('is-join-modal-open');
    document.body.classList.add('is-join-modal-open');

    // The sheet itself, not its first control: a ring on «×» the moment the
    // modal opens reads as a pre-selected close. Tab goes on from here.
    var initial = panelEl;
    if (initial && typeof initial.focus === 'function') initial.focus({ preventScroll: true });

    document.addEventListener('keydown', onKeydown, true);
  }

  function close() {
    if (!state.open) return;
    state.open = false;
    state.titleOpen = '';

    modalEl.classList.remove('is-open');
    modalEl.setAttribute('aria-hidden', 'true');

    document.documentElement.classList.remove('is-join-modal-open');
    document.body.classList.remove('is-join-modal-open');

    document.removeEventListener('keydown', onKeydown, true);

    var styles = window.getComputedStyle(panelEl);
    var duration = parseDuration(styles.transitionDuration);
    var hide = function () { if (!state.open) modalEl.hidden = true; };
    if (duration > 0) setTimeout(hide, duration); else hide();

    if (state.lastOpener && typeof state.lastOpener.focus === 'function') {
      state.lastOpener.focus({ preventScroll: true });
    }
    state.lastOpener = null;
  }

  function onKeydown(e) {
    if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); close(); return; }
    if (e.key === 'Tab') {
      var focusable = getFocusable();
      if (!focusable.length) { e.preventDefault(); panelEl.focus({ preventScroll: true }); return; }
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      var active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || active === panelEl || !panelEl.contains(active)) {
          e.preventDefault(); last.focus({ preventScroll: true });
        }
      } else if (active === last) {
        e.preventDefault(); first.focus({ preventScroll: true });
      }
    }
  }

  function getFocusable() {
    var nodes = panelEl.querySelectorAll(FOCUSABLE);
    return Array.prototype.filter.call(nodes, function (el) {
      return !el.hasAttribute('disabled') && el.offsetParent !== null;
    });
  }

  /* ── Tabs with slide direction ── */
  function switchTab(name, skipAnimation) {
    var fromIdx = TAB_ORDER[state.currentTab] || 0;
    var toIdx = TAB_ORDER[name] || 0;
    var slideFrom = toIdx > fromIdx ? 'right' : 'left';
    state.currentTab = name;

    var tabTitle = '';
    modalEl.querySelectorAll('[data-join-tab]').forEach(function (t) {
      var active = t.getAttribute('data-join-tab') === name;
      t.classList.toggle('is-active', active);
      t.setAttribute('aria-selected', active ? 'true' : 'false');
      if (active) tabTitle = t.getAttribute('data-join-tab-title') || '';
    });

    // The ambassador tab is money for Galas itself, so it says so in the head.
    if (titleEl) titleEl.textContent = tabTitle || state.titleOpen || state.titleDefault;
    modalEl.querySelectorAll('[data-modal-icon]').forEach(function (el) {
      el.hidden = el.getAttribute('data-modal-icon') !== (tabTitle ? 'alt' : 'main');
    });

    modalEl.querySelectorAll('[data-join-panel]').forEach(function (p) {
      var isActive = p.getAttribute('data-join-panel') === name;
      var wasActive = p.classList.contains('is-active');
      if (isActive && !wasActive && !skipAnimation) {
        p.classList.remove('slide-from-left', 'slide-from-right');
        void p.offsetWidth;
        p.classList.add('slide-from-' + slideFrom);
      }
      p.classList.toggle('is-active', isActive);
      p.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });
  }

  /* ── Subscription amount chips ── */
  function initSubAmounts() {
    if (!subChipsEl) return;
    var activeChip = subChipsEl.querySelector('.is-active[data-join-sub-amount]');
    var defaultVal = activeChip ? activeChip.getAttribute('data-join-sub-amount') : '20';
    state.subAmount = parseAmount(defaultVal);
    var chips = subChipsEl.querySelectorAll('[data-join-sub-amount]');
    chips.forEach(function (c) {
      c.classList.remove('is-active');
      c.setAttribute('aria-checked', 'false');
    });
    var match = subChipsEl.querySelector('[data-join-sub-amount="' + cssEscape(String(defaultVal)) + '"]');
    if (!match) match = chips[0];
    if (match) {
      match.classList.add('is-active');
      match.setAttribute('aria-checked', 'true');
      state.subAmount = parseAmount(match.getAttribute('data-join-sub-amount'));
    }
  }

  function selectSubChip(chip) {
    subChipsEl.querySelectorAll('[data-join-sub-amount]').forEach(function (c) {
      c.classList.remove('is-active');
      c.setAttribute('aria-checked', 'false');
    });
    chip.classList.add('is-active');
    chip.setAttribute('aria-checked', 'true');
    state.subAmount = parseAmount(chip.getAttribute('data-join-sub-amount'));
    updateSubCta();
    clearSubError();
  }

  /* ── Join-tab voluntary donation amount chips ── */
  function initJoinAmount() {
    if (!joinChipsEl) return;
    state.joinIsOther = false;
    if (joinCustomInputEl) joinCustomInputEl.value = '';
    var value = joinDefaults.defaultAmount;
    joinChipsEl.querySelectorAll('[data-join-amount]').forEach(function (c) {
      c.classList.remove('is-active');
      c.setAttribute('aria-checked', 'false');
    });
    var match = joinChipsEl.querySelector('[data-join-amount="' + cssEscape(String(value)) + '"]');
    if (!match) match = joinChipsEl.querySelector('[data-join-amount]:not([data-join-amount="other"])');
    if (match) {
      match.classList.add('is-active');
      match.setAttribute('aria-checked', 'true');
      state.joinAmount = parseAmount(match.getAttribute('data-join-amount'));
    }
  }

  function activateJoinChip(chip) {
    if (!joinChipsEl) return;
    joinChipsEl.querySelectorAll('[data-join-amount]').forEach(function (c) {
      c.classList.remove('is-active');
      c.setAttribute('aria-checked', 'false');
    });
    chip.classList.add('is-active');
    chip.setAttribute('aria-checked', 'true');
  }

  function selectJoinChip(chip) {
    var value = chip.getAttribute('data-join-amount');
    if (value === 'other') {
      state.joinIsOther = true;
      state.joinAmount = parseAmount(joinCustomInputEl ? joinCustomInputEl.value : '');
      activateJoinChip(chip);
      if (joinCustomInputEl) joinCustomInputEl.focus({ preventScroll: true });
    } else {
      state.joinIsOther = false;
      if (joinCustomInputEl) joinCustomInputEl.value = '';
      state.joinAmount = parseAmount(value);
      activateJoinChip(chip);
    }
  }

  function updateSubCta() {
    var labelEl = modalEl.querySelector('[data-join-subscribe-label]');
    if (!labelEl) return;

    var freqInput = modalEl.querySelector('[data-join-freq-input]:checked');
    var suffix = freqInput ? (freqInput.getAttribute('data-freq-suffix') || '') : '';
    var amount = state.subAmount;
    var subCur = subChipsEl ? (subChipsEl.getAttribute('data-sub-currency') || '') : '';
    var base = t('join_modal.sub_cta', 'Долучитися й підписатися');

    if (amount && amount > 0) {
      labelEl.textContent = base + ' — ' + formatAmount(amount) + ' ' + subCur + suffix;
    } else {
      labelEl.textContent = base;
    }
  }

  /* ── Login state ── */
  function applyLoginState() {
    var loggedIn = state.isLoggedIn;
    modalEl.querySelectorAll('[data-join-contact]').forEach(function (el) { el.hidden = loggedIn; });
    modalEl.querySelectorAll('[data-join-logged-in]').forEach(function (el) { el.hidden = !loggedIn; });
    if (loggedIn) {
      modalEl.querySelectorAll('[data-join-customer-name]').forEach(function (el) {
        el.textContent = state.customerName || '';
      });
    }
  }

  /* ── Submit: join only ── */
  function onJoinSubmit(e) {
    e.preventDefault();
    clearErrors();

    var panel = modalEl.querySelector('[data-join-panel="join"]');
    if (!panel) return;

    var nameEl = panel.querySelector('[data-join-name]');
    var emailEl = panel.querySelector('[data-join-email]');
    var submitEl = panel.querySelector('[data-join-submit]');
    var errorEl = panel.querySelector('[data-join-error]');
    var nlEl = modalEl.querySelector('[data-join-newsletter]');
    var wantsNewsletter = nlEl ? nlEl.checked : false;

    var name, email;
    if (state.isLoggedIn && state.customer) {
      name = state.customerName ||
        (((state.customer.first_name || '') + ' ' + (state.customer.last_name || '')).trim());
      email = state.customer.email || '';
    } else {
      name = nameEl ? nameEl.value.trim() : '';
      email = emailEl ? emailEl.value.trim() : '';

      // Both fields are required for guests
      if (!name) {
        showError(errorEl, t('join_modal.error_name', 'Введіть ваше імʼя'));
        if (nameEl) nameEl.focus({ preventScroll: true });
        return;
      }
      if (!email || !isValidEmail(email)) {
        showError(errorEl, t('join_modal.error_email', 'Введіть коректний email'));
        if (emailEl) emailEl.focus({ preventScroll: true });
        return;
      }
    }

    if (submitEl) submitEl.disabled = true;

    var member = recordJoin(name, email, 'join', wantsNewsletter);
    var donationAmount = state.joinAmount;
    var finish = function () {
      if (submitEl) submitEl.disabled = false;
      showSuccess(member.code, donationAmount, name);
    };
    submitContact(email, name, wantsNewsletter, member.code).then(finish, finish);
  }

  /* ── Submit: join + subscribe ── */
  function onSubscribeClick(e) {
    e.preventDefault();
    clearSubError();

    var freqInput = modalEl.querySelector('[data-join-freq-input]:checked');
    if (!freqInput) {
      showSubError(t('join_modal.error_tier', 'Оберіть частоту підписки'));
      return;
    }

    var amount = state.subAmount;
    if (!amount || amount <= 0) {
      showSubError(t('join_modal.error_amount', 'Будь ласка, оберіть суму'));
      return;
    }

    var planId = subPlans[freqInput.value] || '';
    var variantId = subVariants[String(amount)] || '';
    if (!planId || !variantId) {
      showSubError(t('join_modal.error_generic', 'Підписка ще не налаштована. Зверніться до адміністратора.'));
      return;
    }

    // The ambassador modal takes it from here: name, email, Telegram, and a cart
    // with exactly one tier (snippets/ambassador-modal.liquid).
    if (window.GalasAmbassador) {
      var tierIndex = { '25': 1, '50': 2, '100': 3 }[String(amount)];
      close();
      window.GalasAmbassador.open({ tier: tierIndex });
      return;
    }
    var props = { _ambassador: '1' };

    var btn = e.currentTarget;
    btn.disabled = true;

    var customer = state.customer || {};
    var name = state.customerName ||
      (((customer.first_name || '') + ' ' + (customer.last_name || '')).trim());
    var email = customer.email || '';

    var nlEl = modalEl.querySelector('[data-join-sub-newsletter]');
    var wantsNewsletter = nlEl ? nlEl.checked : false;

    var member = recordJoin(name, email, 'subscribe', wantsNewsletter);
    // Through the cart API, not a /cart/… permalink: permalinks drop
    // `selling_plan` and checkout sells the variant once.
    var root = ((window.Shopify || {}).routes || {}).root || '/';
    var go = function () {
      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1, selling_plan: Number(planId), properties: props }] })
      }).then(function (res) {
        if (!res.ok) throw new Error('cart/add ' + res.status);
        window.location.href = root + 'checkout';
      }).catch(function () {
        btn.disabled = false;
        showSubError(t('join_modal.error_generic', ''));
      });
    };
    submitContact(email, name, wantsNewsletter, member.code).then(go, go);
  }

  /* ── Member record + persistence ── */
  function recordJoin(name, email, flow, wantsNewsletter) {
    var member = loadMember();
    if (!member || !member.code) {
      member = {
        code: generateCode(),
        name: name || '',
        email: email || '',
        joinedAt: new Date().toISOString()
      };
    } else {
      if (name) member.name = name;
      if (email) member.email = email;
    }
    try { localStorage.setItem(MEMBER_KEY, JSON.stringify(member)); } catch (_) {}

    sendToSheet(member, flow, wantsNewsletter);
    markJoinedTriggers();
    return member;
  }

  function generateCode() {
    var rand = '';
    try {
      var buf = new Uint32Array(1);
      window.crypto.getRandomValues(buf);
      rand = buf[0].toString(36).toUpperCase();
    } catch (_) {
      rand = Math.floor(Math.random() * 60466176).toString(36).toUpperCase();
    }
    rand = (rand + '000000').slice(0, 4);
    var time = (Date.now() % 1679616).toString(36).toUpperCase(); // 2 to 4 chars
    return 'GLS-' + time + rand;
  }

  function loadMember() {
    try { return JSON.parse(localStorage.getItem(MEMBER_KEY)) || null; } catch (_) { return null; }
  }

  /* Mirror the join into Google Sheets (same Apps Script as contact forms).
     The sheet row carries the member code so it can be verified later and
     used by the script to email the participant their code. */
  function sendToSheet(member, flow, wantsNewsletter) {
    if (!sheets.endpoint || !sheets.token) return;
    var amount = flow === 'subscribe' ? state.subAmount : state.joinAmount;
    var currency = joinDefaults.currency || '';
    if (flow === 'subscribe' && subChipsEl) {
      currency = subChipsEl.getAttribute('data-sub-currency') || currency;
    }
    var payload = {
      token: sheets.token,
      form: 'join',
      code: member.code,
      name: member.name,
      email: member.email,
      flow: flow,
      amount: (amount && amount > 0) ? amount : '',
      currency: currency,
      event: state.eventId || '',
      newsletter: wantsNewsletter ? 'yes' : 'no',
      page: window.location.href
    };
    try {
      fetch(sheets.endpoint, {
        method: 'POST',
        mode: 'no-cors',
        keepalive: true,
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body: JSON.stringify(payload)
      });
    } catch (_) { /* never block the join flow */ }
  }

  /* ── Joined state on triggers ── */
  function markJoinedTriggers() {
    var member = loadMember();
    if (!member || !member.code) return;
    document.querySelectorAll('[data-join-trigger]').forEach(function (btn) {
      btn.classList.add('is-joined');
      var labelEl = btn.querySelector('.button__label');
      if (labelEl) labelEl.textContent = member.code + ' ✓';
    });
  }

  /* ── Success screen ── */
  function showSuccess(code, amount, donorName) {
    if (formEl) formEl.hidden = true;
    if (successCodeEl) successCodeEl.textContent = code || '';
    updateDonateLink(amount, donorName);
    if (successEl) successEl.hidden = false;
    var closeBtn = successEl ? successEl.querySelector('[data-join-modal-close]') : null;
    if (closeBtn) closeBtn.focus({ preventScroll: true });
  }

  /* Point the "support with a donation" button at the Monobank jar with the
     chosen (or default) amount. Opens in a new tab so the code stays visible. */
  function updateDonateLink(amount, donorName) {
    if (!donateLinkEl) return;
    var jar = (joinDefaults.jarUrl || '').trim();
    var amt = (amount && amount > 0) ? amount : parseAmount(joinDefaults.defaultAmount);
    if (!jar || !amt) { donateLinkEl.hidden = true; return; }
    donateLinkEl.href = buildDonateUrl(jar, amt, donorName || '');
    if (donateLabelEl) {
      var base = t('join_modal.donate_cta', 'Підтримати донатом');
      donateLabelEl.textContent = base + ' — ' + joinDefaults.currency + formatAmount(amt);
    }
    donateLinkEl.hidden = false;
  }

  function buildDonateUrl(base, amount, name) {
    try {
      var url = new URL(base, window.location.origin);
      url.searchParams.set('a', String(amount));
      if (name) url.searchParams.set('t', name);
      return url.toString();
    } catch (_) {
      var sep = base.indexOf('?') === -1 ? '?' : '&';
      var qs = 'a=' + encodeURIComponent(String(amount));
      if (name) qs += '&t=' + encodeURIComponent(name);
      return base + sep + qs;
    }
  }

  function hideSuccess() {
    if (formEl) formEl.hidden = false;
    if (successEl) successEl.hidden = true;
  }

  /* ── Helpers ── */
  function resetContactFields() {
    var member = loadMember();
    modalEl.querySelectorAll('[data-join-name]').forEach(function (el) { el.value = (member && member.name) || ''; });
    modalEl.querySelectorAll('[data-join-email]').forEach(function (el) { el.value = (member && member.email) || ''; });
  }

  function clearErrors() {
    modalEl.querySelectorAll('[data-join-error], [data-join-sub-error]').forEach(function (el) {
      el.textContent = '';
      el.hidden = true;
    });
  }

  function showError(errorEl, msg) {
    if (!errorEl) return;
    errorEl.textContent = msg;
    errorEl.hidden = false;
  }

  function showSubError(msg) {
    showError(modalEl.querySelector('[data-join-sub-error]'), msg);
  }

  function clearSubError() {
    var el = modalEl.querySelector('[data-join-sub-error]');
    if (el) { el.textContent = ''; el.hidden = true; }
  }

  // Creates/updates a Shopify customer (Admin → Customers), tagged with the member code.
  function submitContact(email, name, acceptsMarketing, code) {
    if (!email) return Promise.resolve();
    var tags = ['join-event'];
    if (code) tags.push('member-' + code);
    if (state.eventId) tags.push('event-' + state.eventId);
    var params = new URLSearchParams();
    params.set('form_type', 'customer');
    params.set('utf8', '\u2713');
    params.set('contact[email]', email);
    params.set('contact[tags]', tags.join(','));
    // Never send "false": it could revoke consent for an already-subscribed email
    if (acceptsMarketing) params.set('contact[accepts_marketing]', 'true');
    if (name) {
      var parts = name.split(/\s+/);
      params.set('contact[first_name]', parts[0]);
      if (parts.length > 1) params.set('contact[last_name]', parts.slice(1).join(' '));
    }
    return fetch('/contact#contact_form', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'text/html'
      },
      body: params.toString()
    }).catch(function () {});
  }

  function isValidEmail(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v); }

  function formatAmount(v) {
    try { return new Intl.NumberFormat('uk', { maximumFractionDigits: 0 }).format(v); }
    catch (_) { return String(Math.round(v)); }
  }

  function parseAmount(raw) {
    if (raw == null) return null;
    var num = parseFloat(String(raw).replace(/[^0-9.,]/g, '').replace(',', '.'));
    if (isNaN(num) || num <= 0) return null;
    return Math.round(num);
  }

  function parseDuration(v) {
    if (!v) return 0;
    var max = 0;
    v.split(',').forEach(function (s) {
      var ms = s.trim().endsWith('ms') ? parseFloat(s) : parseFloat(s) * 1000;
      if (!isNaN(ms) && ms > max) max = ms;
    });
    return max;
  }

  function t(key, fallback) { return (window.JoinModalI18n || {})[key] || fallback; }

  function cssEscape(v) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(v);
    return String(v).replace(/[^a-zA-Z0-9_-]/g, function (c) { return '\\' + c; });
  }

  window.JoinModal = {
    open: function (tab) { open(null, tab || 'join'); },
    close: close
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
